import { Provider } from "jotai";
import { Suspense, useEffect } from "react";
import { useQueryLoader } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";

import { BookingWizardQueryRenderer } from "./components/BookingWizardQueryRenderer";
import { BookingWizardQueryRendererQueryString } from "./components/BookingWizardQueryRenderer/BookingWizardQueryRenderer";
import {
  BookingWizardQueryRendererQuery,
  SlotType,
} from "./components/BookingWizardQueryRenderer/__generated__/BookingWizardQueryRendererQuery.graphql";
import { BookingWizard as BookingWizardType } from "./types";
import { canLoadBookingWizardQuery } from "./utils/canLoadBookingWizardQuery";

type BookingWizardProps = {
  isOpen: boolean;
  onDismiss: () => void;
  onCancel?: () => void;
  slotId: string | null;
  initiallyLockedSlotIds?: readonly string[];
  isSlotLockAcquired?: boolean;
  membershipTypeId: string | null;
  currentPatronId: string;
  currentDate: string;
  clubSlug: string;
  slotType: SlotType;
  facilityId?: string;
  initializationOverides?: BookingWizardType.BookingWizardInitializationOverides;
};

export const BookingWizard = ({
  isOpen,
  onDismiss,
  onCancel,
  slotId,
  initiallyLockedSlotIds = [],
  isSlotLockAcquired = true,
  membershipTypeId,
  currentPatronId,
  currentDate,
  clubSlug,
  slotType,
  facilityId,
  initializationOverides,
}: BookingWizardProps) => {
  return (
    <StandardModal
      isOpen={isOpen}
      onClose={() => {}}
      hideCloseButton={true}
      disableContentScroll={true}
      content={
        <BookingWizardContent
          currentPatronId={currentPatronId}
          currentDate={currentDate}
          onDismiss={onDismiss}
          onCancel={onCancel}
          slotId={slotId}
          initiallyLockedSlotIds={initiallyLockedSlotIds}
          isSlotLockAcquired={isSlotLockAcquired}
          isOpen={isOpen}
          membershipTypeId={membershipTypeId}
          clubSlug={clubSlug}
          slotType={slotType}
          facilityId={facilityId}
          initializationOverides={{
            ...initializationOverides,
            groupSize:
              initializationOverides?.groupSize === 0
                ? 1
                : initializationOverides?.groupSize,
          }}
        />
      }
    />
  );
};

type BookingWizardContentProps = BookingWizardProps;

const BookingWizardContent = ({
  slotId,
  initiallyLockedSlotIds,
  isSlotLockAcquired = true,
  isOpen,
  onDismiss,
  onCancel,
  membershipTypeId,
  currentPatronId,
  currentDate,
  clubSlug,
  slotType,
  facilityId,
  initializationOverides,
}: BookingWizardContentProps) => {
  // Standard booking query loader
  const [queryRef, loadQuery] = useQueryLoader<BookingWizardQueryRendererQuery>(
    BookingWizardQueryRendererQueryString
  );

  useEffect(() => {
    if (
      canLoadBookingWizardQuery({
        currentPatronId,
        isOpen,
        isSlotLockAcquired,
        membershipTypeId,
        slotId,
      }) &&
      slotId &&
      membershipTypeId
    ) {
      // Load standard booking query
      loadQuery(
        {
          slotId,
          membershipTypeId,
          lessonTypeId: undefined,
          players: [currentPatronId],
          currentDate,
          slotType,
          clubSlug,
          facilityId: facilityId || "",
          bookingType: "STANDARD",
          memberId: currentPatronId,
        },
        { fetchPolicy: "network-only" }
      );
    }
  }, [
    loadQuery,
    slotId,
    isOpen,
    isSlotLockAcquired,
    membershipTypeId,
    currentPatronId,
    currentDate,
    clubSlug,
    slotType,
    facilityId,
  ]);

  return (
    <Provider key={`${slotId ?? "closed"}:${isOpen}`}>
      <Suspense fallback={<Loader />}>
        {queryRef && isSlotLockAcquired ? (
          <BookingWizardQueryRenderer
            memberId={currentPatronId}
            queryRef={queryRef}
            initiallyLockedSlotIds={initiallyLockedSlotIds}
            onDismiss={onDismiss}
            onCancel={onCancel}
            initializationOverides={initializationOverides}
          />
        ) : (
          <Loader />
        )}
      </Suspense>
    </Provider>
  );
};
